import { Preferences } from '@capacitor/preferences';
import type { IndicatorDoc } from '@core/sim/indicators/dsl/schema';
import type { IndicatorTemplate } from '@core/sim/indicators/dsl/templates';
import { validateDsl } from '@core/sim/indicators/dsl/validate';

const KEY = 'sotda-custom-indicators';
export const MAX_CUSTOM_INDICATORS = 12;

export type CustomIndicatorItem = {
  id: string;
  name: string;
  source: 'template' | 'ai' | 'community' | 'studio';
  templateId?: string;
  pane: 'main' | 'sub';
  doc: IndicatorDoc;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
};

export type CustomIndicatorStore = { version: 1; items: CustomIndicatorItem[] };

const emptyStore = (): CustomIndicatorStore => ({ version: 1, items: [] });

export function normalizeCustomStore(raw: unknown): CustomIndicatorStore {
  if (!raw || typeof raw !== 'object' || (raw as { version?: unknown }).version !== 1 || !Array.isArray((raw as { items?: unknown }).items)) return emptyStore();
  const items: CustomIndicatorItem[] = [];
  for (const candidate of (raw as { items: unknown[] }).items) {
    if (!candidate || typeof candidate !== 'object') continue;
    const item = candidate as Record<string, unknown>;
    if (typeof item.id !== 'string' || !item.id || typeof item.name !== 'string' || !item.name || item.name.length > 20) continue;
    if (item.source !== 'template' && item.source !== 'ai' && item.source !== 'community' && item.source !== 'studio') continue;
    if (item.pane !== 'main' && item.pane !== 'sub') continue;
    if (typeof item.enabled !== 'boolean' || typeof item.createdAt !== 'number' || !Number.isFinite(item.createdAt) || typeof item.updatedAt !== 'number' || !Number.isFinite(item.updatedAt)) continue;
    if (item.templateId !== undefined && typeof item.templateId !== 'string') continue;
    const validated = validateDsl(item.doc);
    if (!validated.ok) continue;
    items.push({
      id: item.id,
      name: item.name,
      source: item.source,
      ...(typeof item.templateId === 'string' ? { templateId: item.templateId } : {}),
      pane: item.pane,
      doc: validated.doc,
      enabled: item.enabled,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    });
    if (items.length >= MAX_CUSTOM_INDICATORS) break;
  }
  return { version: 1, items };
}

export async function getCustomIndicators(): Promise<CustomIndicatorStore> {
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (!value) return emptyStore();
    return normalizeCustomStore(JSON.parse(value));
  } catch {
    return emptyStore();
  }
}

export async function setCustomIndicators(store: CustomIndicatorStore): Promise<void> {
  try {
    await Preferences.set({ key: KEY, value: JSON.stringify(normalizeCustomStore(store)) });
  } catch {
    /* 저장 실패는 무시(커스텀 지표는 부가 — 다음 진입 시 빈 목록 폴백) */
  }
}

export function installTemplate(store: CustomIndicatorStore, template: IndicatorTemplate): CustomIndicatorStore {
  const id = `tpl:${template.id}`;
  if (store.items.some((item) => item.id === id) || store.items.length >= MAX_CUSTOM_INDICATORS) return store;
  const validated = validateDsl(template.doc);
  if (!validated.ok) return store;
  const now = Date.now();
  return { ...store, items: [...store.items, { id, name: template.name.slice(0, 20), source: 'template', templateId: template.id, pane: template.pane, doc: validated.doc, enabled: true, createdAt: now, updatedAt: now }] };
}

export function addAiIndicator(store: CustomIndicatorStore, name: string, doc: IndicatorDoc): CustomIndicatorStore {
  if (store.items.length >= MAX_CUSTOM_INDICATORS) return store;
  const validated = validateDsl(doc);
  if (!validated.ok) return store;
  const now = Date.now();
  const id = `ai:${now.toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  return { ...store, items: [...store.items, { id, name: name.slice(0, 20), source: 'ai', pane: 'sub', doc: validated.doc, enabled: true, createdAt: now, updatedAt: now }] };
}

export function updateItem(store: CustomIndicatorStore, id: string, patch: Partial<Pick<CustomIndicatorItem, 'enabled' | 'pane' | 'name'>>): CustomIndicatorStore {
  if (!store.items.some((item) => item.id === id)) return store;
  const safePatch = { ...patch, ...(patch.name !== undefined ? { name: patch.name.slice(0, 20) } : {}) };
  const now = Date.now();
  return { ...store, items: store.items.map((item) => item.id === id ? { ...item, ...safePatch, updatedAt: now } : item) };
}

export function removeItem(store: CustomIndicatorStore, id: string): CustomIndicatorStore {
  if (!store.items.some((item) => item.id === id)) return store;
  return { ...store, items: store.items.filter((item) => item.id !== id) };
}

export function canStartStudioEdit(store: CustomIndicatorStore, origin: { kind: 'new' | 'copy' | 'own' }): boolean {
  return origin.kind === 'own' || store.items.length < MAX_CUSTOM_INDICATORS;
}

export type StudioSavePolicy = 'new' | 'overwrite' | 'conflict';

/** origin과 원장 최신 시각만으로 저장 정책을 결정한다. */
export function resolveStudioSavePolicy(
  origin: { kind: 'new' | 'ai-fresh' | 'copy' } | { kind: 'own'; baseItemId: string },
  baseUpdatedAt: number | null,
  current: CustomIndicatorItem | null,
  conflictChoice?: 'overwrite' | 'copy',
): StudioSavePolicy {
  if (origin.kind !== 'own' || !current) return 'new';
  if (baseUpdatedAt !== null && current.updatedAt > baseUpdatedAt) return conflictChoice === 'overwrite' ? 'overwrite' : conflictChoice === 'copy' ? 'new' : 'conflict';
  return 'overwrite';
}

export function addStudioItem(store: CustomIndicatorStore, name: string, pane: 'main' | 'sub', doc: IndicatorDoc, now = Date.now(), enabled = true): CustomIndicatorStore {
  if (store.items.length >= MAX_CUSTOM_INDICATORS) return store;
  const validated = validateDsl(doc);
  if (!validated.ok) return store;
  const id = `stu:${now.toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  return { ...store, items: [...store.items, { id, name: name.slice(0, 20), source: 'studio', pane, doc: validated.doc, enabled, createdAt: now, updatedAt: now }] };
}

export function overwriteDoc(store: CustomIndicatorStore, id: string, doc: IndicatorDoc, meta: { name: string; pane: 'main' | 'sub' }, now = Date.now()): CustomIndicatorStore {
  const validated = validateDsl(doc);
  if (!validated.ok || !store.items.some((item) => item.id === id)) return store;
  return {
    ...store,
    items: store.items.map((item) => item.id === id ? { ...item, name: meta.name.slice(0, 20), pane: meta.pane, doc: validated.doc, updatedAt: now } : item),
  };
}
